import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import MemberAvatar from './MemberAvatar';
import AttributeLabel from './AttributeLabel';
import {
  UserGroupIcon,
  UserIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface Participant {
  id: string;
  member_id: string;
  mg_nr?: string;
  vorname: string;
  nachname: string;
  name?: string;
  active?: number;
  membership_ended_before_event?: boolean;
  roles?: Array<{ name: string; description?: string; priority: number; icon?: string | null }>;
  is_guest?: boolean;
  card_type?: string;
  issues?: Array<{
    hundename: string;
    problems: string[];
  }>;
  gebucht_am?: string;
}

interface ParticipantsModalProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
}

const ParticipantsModal: React.FC<ParticipantsModalProps> = ({ event, isOpen, onClose }) => {
  const { config } = useContext(AuthContext);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const BASE_URL = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:4000';

  const getPhotoUrl = (participant: Participant): string | undefined => {
    const userId = participant.mg_nr || participant.member_id;
    if (!userId) return undefined;
    return `${BASE_URL}/api/members/user-image-url/${userId}?size=S&token=${localStorage.getItem('jwt_token')}`;
  };

  const markImageError = (key: string) => {
    setImageErrors((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (isOpen) {
      fetchParticipants();
    }
  }, [isOpen]);

  const fetchParticipants = async () => {
    if (!event?.id) return;

    setLoading(true);
    try {
      const response = await apiClient.get(`/api/calendar/${event.id}/participants`);
      if (response.data.success) {
        setParticipants(response.data.participants || []);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
      setError('Fehler beim Laden der Teilnehmer');
    } finally {
      setLoading(false);
    }
  };

  const getParticipantIssues = (participant: Participant) => {
    const tollwutIssues: string[] = [];
    const insuranceIssues: string[] = [];

    participant.issues?.forEach((issue) => {
      issue.problems.forEach((problem) => {
        if (problem.toLowerCase().includes('tollwut')) {
          tollwutIssues.push(`${issue.hundename}: ${problem}`);
        } else if (
          problem.toLowerCase().includes('versicherung') ||
          problem.toLowerCase().includes('haftpflicht')
        ) {
          insuranceIssues.push(`${issue.hundename}: ${problem}`);
        }
      });
    });

    return {
      hasTollwutIssue: tollwutIssues.length > 0,
      tollwutIssues,
      hasInsuranceIssue: insuranceIssues.length > 0,
      insuranceIssues,
    };
  };

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  const bookingCount = event?.extendedProps?.bookingCount || 0;
  const maxBookable = event?.extendedProps?.bookable || 0;

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      variant="detail"
      size="md"
      accent="brand"
      icon={UserGroupIcon}
      title={
        <span className="inline-flex items-center gap-2">
          <span>Teilnehmer</span>
          <span className="px-2 py-0.5 text-xs font-bold bg-info-100 dark:bg-info-900/50 text-info-700 dark:text-info-300 rounded-full">
            {bookingCount}/{maxBookable}
          </span>
        </span>
      }
      subtitle={event?.title}
      footer={<ModalActions secondary={{ label: 'Schließen', onClick: onClose }} />}
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-info-600 border-t-transparent rounded-full mb-4" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Teilnehmer werden geladen...</span>
        </div>
      ) : error ? (
        <div className="p-4 bg-error-50 dark:bg-error-900/20 rounded-xl border border-error-200 dark:border-error-700">
          <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
        </div>
      ) : participants.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
            <UserIcon className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-1">Keine Teilnehmer</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">Für diesen Termin sind noch keine Buchungen vorhanden</p>
        </div>
      ) : (
        <ModalSection title={`Teilnehmer (${participants.length})`}>
          <div className="space-y-3">
            {participants.map((participant) => {
              const { hasTollwutIssue, hasInsuranceIssue } = getParticipantIssues(participant);
              const displayName = `${participant.vorname} ${participant.nachname || participant.name || ''}`.trim();
              const hasAnyIssue = hasTollwutIssue || hasInsuranceIssue;
              const isGuest = !!participant.is_guest;
              const rowKey = participant.member_id || participant.id;

              return (
                <div
                  key={rowKey}
                  className={`flex items-center gap-4 rounded-2xl border px-4 py-4 ${
                    isGuest
                      ? 'border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                  }`}
                >
                  <MemberAvatar
                    member={{
                      vorname: participant.vorname,
                      name: participant.nachname || participant.name,
                      active: participant.active,
                      roles: participant.roles,
                    }}
                    role={isGuest && !(participant.roles && participant.roles.length > 0) ? 'guest' : undefined}
                    photoUrl={getPhotoUrl(participant)}
                    hasImageError={imageErrors.has(rowKey)}
                    onImageError={() => markImageError(rowKey)}
                    size="lg"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-base font-semibold text-gray-900 dark:text-white">{displayName}</p>
                      {participant.membership_ended_before_event && <span className="text-xs font-semibold text-warning-700 dark:text-warning-300">Mitgliedschaft vor diesem Termin beendet</span>}
                      {isGuest && (
                        <span className="flex-shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          Gast
                        </span>
                      )}
                    </div>
                    {hasAnyIssue && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {hasTollwutIssue && (
                          <span className="rounded bg-error-100 px-1.5 py-0.5 dark:bg-error-900/30">
                            <AttributeLabel iconKey="vaccination" size="sm" textClassName="text-xs font-medium text-error-700 dark:text-error-400">Impfung</AttributeLabel>
                          </span>
                        )}
                        {hasInsuranceIssue && (
                          <span className="rounded bg-error-100 px-1.5 py-0.5 dark:bg-error-900/30">
                            <AttributeLabel iconKey="liability" size="sm" textClassName="text-xs font-medium text-error-700 dark:text-error-400">Versicherung</AttributeLabel>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                      hasAnyIssue ? 'bg-error-50 dark:bg-error-900/30' : 'bg-success-50 dark:bg-success-900/30'
                    }`}
                  >
                    {hasAnyIssue ? (
                      <ExclamationTriangleIcon className="h-5 w-5 text-error-500" />
                    ) : (
                      <CheckIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </ModalSection>
      )}
    </ModalShell>
  );
};

export default ParticipantsModal;
